import { type ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "@/utils/cn";

export interface DropdownOption {
  label: string;
  value: string;
  danger?: boolean;
}

export function Dropdown({
  trigger,
  options,
  onSelect,
  align = "right",
}: {
  trigger: ReactNode;
  options: DropdownOption[];
  onSelect: (value: string) => void;
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div className="relative inline-block" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={() => setOpen((o) => !o)}>
        {trigger}
      </button>
      {open && (
        <div
          className={cn(
            "absolute z-20 mt-1 min-w-[10rem] rounded-lg border border-border bg-cream-50 py-1 shadow-popover",
            align === "right" ? "right-0" : "left-0",
          )}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                onSelect(opt.value);
                setOpen(false);
              }}
              className={cn(
                "block w-full px-3 py-2 text-left text-sm hover:bg-cream-200",
                opt.danger ? "text-danger-500" : "text-ink",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
